import { useState, useRef, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { db, auth } from "../services/firebase";
import { collection, query, where, orderBy, getDocs, doc, getDoc } from "firebase/firestore";
import { MessageCircle, X, Send, Loader2, Bot, User } from "lucide-react";
import { sortEntriesByDateAsc } from "../utils/entryDate";
import { buildChatContext } from "../utils/chatContext";
import { chatErrorMessage, MAX_CHAT_INPUT_CHARS } from "../utils/chatErrors";
import type { ChatEntry as Entry, ChatProfil as Profil } from "../utils/chatContext";
import { IS_NATIVE } from "../config/platform";

interface Message {
  role: "user" | "assistant";
  content: string;
}

export default function AiChat() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([
    { role: "assistant", content: "Szia! 👋 A naplód adatai alapján tudok segíteni GLP-1 terápiáddal kapcsolatos kérdésekben. Miben segíthetek?" }
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [entries, setEntries] = useState<Entry[]>([]);
  const [profil, setProfil] = useState<Profil>({});
  const bottomRef = useRef<HTMLDivElement>(null);

  // Adatok betöltése minden megnyitáskor (friss adat az új bejegyzésekhez)
  useEffect(() => {
    if (!open || !user) return;
    const load = async () => {
      try {
        const q = query(
          collection(db, "entries"),
          where("userId", "==", user.uid),
          orderBy("createdAt", "asc")
        );
        const snap = await getDocs(q);
        // A Firestore-lekérdezés createdAt (beviteli idő) szerint rendez —
        // ez visszamenőleges rögzítésnél rossz sorrendet ad (pl. az AI
        // téves "jelenlegi súlyt"/"utolsó bejegyzéseket" kapna kontextusul).
        // Minden idősor a date (mikor történt) szerint rendezve legyen.
        const e = sortEntriesByDateAsc(snap.docs.map(d => d.data() as Entry));
        setEntries(e);

        const pSnap = await getDoc(doc(db, "profiles", user.uid));
        if (pSnap.exists()) setProfil(pSnap.data() as Profil);
      } catch (err) {
        console.error("Chat context load error:", err);
      }
    };
    load();
  }, [open, user]);

  // Auto scroll
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  const sendMessage = async () => {
    if (!input.trim() || loading) return;

    const userMsg: Message = { role: "user", content: input.trim() };
    setMessages(prev => [...prev, userMsg]);
    setInput("");
    setLoading(true);

    try {
      const token = await auth.currentUser?.getIdToken();
      const allMessages = [...messages, userMsg];
      const response = await fetch("https://europe-west1-tesztapp-a812e.cloudfunctions.net/claudeProxy", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        },
        body: JSON.stringify({
          max_tokens: 1000,
          context: buildChatContext(entries, profil, IS_NATIVE ? "native" : "web"),
          messages: allMessages.slice(-10).map(m => ({
            role: m.role,
            content: m.content,
          })),
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        // A3 (2026-10-14): a szerver hibakódjai — napi limit, túl hosszú üzenet,
        // nem megerősített e-mail, átmeneti túlterhelés — saját szöveget kapnak.
        setMessages(prev => [...prev, {
          role: "assistant",
          content: chatErrorMessage(typeof errorData.error === "string" ? errorData.error : undefined),
        }]);
        return;
      }

      const data = await response.json();
      const text = data.content?.[0]?.text || "Sajnálom, nem tudtam válaszolni.";
      setMessages(prev => [...prev, { role: "assistant", content: text }]);
    } catch {
      setMessages(prev => [...prev, { role: "assistant", content: "Hiba történt. Kérlek próbáld újra." }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Lebegő gomb */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed right-4 w-14 h-14 bg-emerald-700 text-white rounded-full shadow-lg flex items-center justify-center hover:bg-emerald-800 transition-all hover:scale-110 z-50"
          style={{ bottom: 'calc(env(safe-area-inset-bottom) + 90px)' }}
        >
          <MessageCircle size={24} />
        </button>
      )}

      {/* Chat ablak */}
      {open && (
        <div className="fixed inset-0 z-50 flex flex-col bg-white"
          style={{ paddingTop: 'env(safe-area-inset-top)', paddingBottom: 'env(safe-area-inset-bottom)' }}>
          
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-white">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-emerald-100 rounded-full flex items-center justify-center">
                <Bot size={18} className="text-emerald-700" />
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-900">GLP Asszisztens</p>
                <p className="text-xs text-gray-400">A te adataid alapján</p>
              </div>
            </div>
            <button onClick={() => setOpen(false)}
              className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors">
              <X size={18} className="text-gray-500" />
            </button>
          </div>

          {/* Disclaimer */}
          <div className="mx-4 mt-3 bg-yellow-50 border border-yellow-100 rounded-xl px-3 py-2">
            <p className="text-xs text-yellow-700">⚕️ Ez nem orvosi tanácsadás. Egészségügyi kérdésekben mindig fordulj kezelőorvosodhoz.</p>
          </div>

          {/* Üzenetek */}
          <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
            {messages.map((msg, i) => (
              <div key={i} className={`flex gap-2 ${msg.role === "user" ? "flex-row-reverse" : "flex-row"}`}>
                <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${
                  msg.role === "assistant" ? "bg-emerald-100" : "bg-gray-100"
                }`}>
                  {msg.role === "assistant"
                    ? <Bot size={14} className="text-emerald-700" />
                    : <User size={14} className="text-gray-600" />
                  }
                </div>
                <div className={`max-w-[80%] px-3 py-2.5 rounded-2xl text-sm leading-relaxed ${
                  msg.role === "assistant"
                    ? "bg-gray-50 text-gray-800 rounded-tl-sm"
                    : "bg-emerald-700 text-white rounded-tr-sm"
                }`}>
                  {msg.content}
                </div>
              </div>
            ))}

            {loading && (
              <div className="flex gap-2 items-center">
                <div className="w-7 h-7 rounded-full bg-emerald-100 flex items-center justify-center">
                  <Bot size={14} className="text-emerald-700" />
                </div>
                <div className="bg-gray-50 px-3 py-2.5 rounded-2xl rounded-tl-sm">
                  <Loader2 size={16} className="text-emerald-600 animate-spin" />
                </div>
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          {/* Gyors kérdések */}
          {messages.length === 1 && (
            <div className="px-4 pb-2 flex gap-2 overflow-x-auto">
              {[
                "Hogy haladok?",
                "Mit jelent a peak fázis?",
                "Miért stagnál a súlyom?",
                "Mikor várható eredmény?",
              ].map(q => (
                <button key={q} onClick={() => setInput(q)}
                  className="flex-shrink-0 bg-emerald-50 text-emerald-700 text-xs px-3 py-1.5 rounded-full border border-emerald-100 hover:bg-emerald-100 transition-colors">
                  {q}
                </button>
              ))}
            </div>
          )}

          {/* Input */}
          <div className="px-4 py-3 border-t border-gray-100 flex gap-2">
            <input
              type="text"
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => e.key === "Enter" && sendMessage()}
              maxLength={MAX_CHAT_INPUT_CHARS}
              placeholder="Írj kérdést..."
              className="flex-1 min-w-0 border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
            />
            <button
              onClick={sendMessage}
              disabled={!input.trim() || loading}
              className="w-10 h-10 bg-emerald-700 text-white rounded-xl flex items-center justify-center disabled:opacity-40 hover:bg-emerald-800 transition-colors"
            >
              <Send size={16} />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
